import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import api from '../lib/api';
import { toast } from 'sonner';
import {
  Calculator,
  Users,
  Building2,
  AppWindow,
  CreditCard,
  Bell,
  ExternalLink,
  Clock,
  RefreshCw,
  AlertCircle,
  Sparkles,
  CalendarDays,
  Eye,
  Target,
  Loader2,
} from 'lucide-react';

const APP_ICONS = {
  'nexus-compta': Calculator,
  'nexus-sirh': Users,
  'nexus-crm': Target,
  compta: Calculator,
  sirh: Users,
};

const APP_NAMES = {
  'nexus-compta': 'Nexus Compta',
  'nexus-sirh': 'Nexus SIRH',
  'nexus-crm': 'Nexus CRM',
};

const APP_URLS = {
  'nexus-compta': 'https://compta.nexus-360.fr',
  'nexus-sirh': 'https://sirh.nexus-360.fr',
};

const STATUS_BADGES = {
  active: { label: 'Actif', className: 'bg-green-100 text-green-700' },
  trial: { label: 'Essai', className: 'bg-yellow-100 text-yellow-700' },
  suspended: { label: 'Suspendu', className: 'bg-red-100 text-red-700' },
  inactive: { label: 'Inactif', className: 'bg-slate-100 text-slate-500' },
  cancelled: { label: 'Annule', className: 'bg-red-100 text-red-700' },
};

const Dashboard = () => {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [apps, setApps] = useState([]);
  // Code du module en cours d'ouverture par le flux SSO callback.
  const [launching, setLaunching] = useState(null);

  // Deux modes d'ouverture, decides par le catalogue :
  //  - `callback` : jeton a usage unique demande a l'API, puis redirection
  //    vers le callback du module (nexus-crm) ;
  //  - `legacy`   : l'app recoit le jeton de session dans l'URL (compta, sirh).
  const openApp = async (app) => {
    setLaunching(app.code);
    try {
      const res = await api.post('/api/sso/generate-token', { productSlug: app.code });
      const redirectUrl = res.data?.data?.redirectUrl;
      if (!redirectUrl) throw new Error('missing redirectUrl');
      window.location.assign(redirectUrl);
    } catch (err) {
      const status = err.response?.status;
      const message =
        status === 403
          ? "Vous n'avez pas acces a ce module. Demandez a l'administrateur de votre structure."
          : status === 404
            ? "Ce module n'est pas encore ouvert."
            : err.response?.data?.detail || "Impossible d'ouvrir le module pour le moment.";
      toast.error(message);
      setLaunching(null);
    }
  };
  const [subscriptions, setSubscriptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [statsRes, notifsRes, appsRes, subsRes] = await Promise.allSettled([
        api.get('/api/dashboard/stats'),
        api.get('/api/notifications'),
        api.get('/api/apps'),
        api.get('/api/subscriptions/current-detailed'),
      ]);

      if (statsRes.status === 'fulfilled') setStats(statsRes.value.data);
      if (notifsRes.status === 'fulfilled') {
        const data = notifsRes.value.data;
        const notifList = Array.isArray(data) ? data : data.notifications || data.items || [];
        setNotifications(notifList.slice(0, 10));
      }
      if (appsRes.status === 'fulfilled') {
        setApps(Array.isArray(appsRes.value.data) ? appsRes.value.data : appsRes.value.data.items || []);
      }
      if (subsRes.status === 'fulfilled') {
        const data = subsRes.value.data;
        setSubscriptions(Array.isArray(data) ? data : data.items || []);
      }
    } catch (err) {
      setError('Impossible de charger les donnees du tableau de bord');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const userName = user?.prenom || user?.first_name || '';
  const structureName = user?.structure?.nom || user?.structure_name || user?.nom_structure || '';

  // User has access if they have a subscription OR an app_role for the app
  // subscriptions now come from /current-detailed: [{app_code, is_subscribed, ...}]
  const subscribedAppCodes = [
    ...subscriptions.filter((s) => s.is_subscribed).map((s) => s.app_code),
    ...subscriptions.filter((s) => !s.is_subscribed).map((s) => s.app_code), // app_roles included by backend
    ...Object.keys(user?.app_roles || {}),
  ].filter(Boolean);
  const uniqueSubscribedCodes = [...new Set(subscribedAppCodes)];

  const statCards = [
    { label: 'Structures', value: stats?.structures_count ?? stats?.structures ?? '--', icon: Building2, color: 'bg-blue-50 text-blue-600' },
    { label: 'Utilisateurs', value: stats?.users_count ?? stats?.users ?? '--', icon: Users, color: 'bg-green-50 text-green-600' },
    { label: 'Applications', value: stats?.apps_count ?? stats?.apps ?? '--', icon: AppWindow, color: 'bg-purple-50 text-purple-600' },
    { label: 'Abonnements actifs', value: stats?.active_subscriptions ?? stats?.subscriptions ?? '--', icon: CreditCard, color: 'bg-amber-50 text-amber-600' },
  ];

  if (loading) {
    return (
      <div className="space-y-8">
        {/* Welcome skeleton */}
        <div className="bg-gradient-to-r from-nexus-600 to-nexus-700 rounded-2xl p-8 animate-pulse">
          <div className="h-8 bg-white/20 rounded w-64 mb-3" />
          <div className="h-5 bg-white/10 rounded w-96" />
        </div>
        {/* Stats skeleton */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="bg-white rounded-xl border border-slate-200 p-6 animate-pulse">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-slate-100 rounded-lg" />
                <div className="space-y-2">
                  <div className="h-4 bg-slate-100 rounded w-20" />
                  <div className="h-6 bg-slate-100 rounded w-12" />
                </div>
              </div>
            </div>
          ))}
        </div>
        {/* App cards skeleton */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {[1, 2, 3].map((i) => (
            <div key={i} className="bg-white rounded-xl border border-slate-200 p-6 animate-pulse">
              <div className="h-12 bg-slate-100 rounded w-12 mb-4" />
              <div className="h-5 bg-slate-100 rounded w-32 mb-2" />
              <div className="h-4 bg-slate-100 rounded w-48" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center py-20">
        <AlertCircle className="w-12 h-12 text-red-400 mb-4" />
        <h3 className="text-lg font-semibold text-slate-900 mb-2">Erreur de chargement</h3>
        <p className="text-slate-500 mb-6">{error}</p>
        <button
          onClick={fetchData}
          className="flex items-center gap-2 px-4 py-2 rounded-lg bg-nexus-600 text-white text-sm font-medium hover:bg-nexus-700 transition-colors"
        >
          <RefreshCw className="w-4 h-4" />
          Reessayer
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Welcome Banner */}
      <div className="bg-gradient-to-r from-nexus-600 to-nexus-700 rounded-2xl p-8 text-white relative overflow-hidden">
        <div className="absolute top-0 right-0 w-64 h-64 bg-white/5 rounded-full -translate-y-1/2 translate-x-1/3" />
        <div className="absolute bottom-0 left-1/2 w-48 h-48 bg-white/5 rounded-full translate-y-1/2" />
        <div className="relative z-10">
          <div className="flex items-center gap-2 mb-2">
            <Sparkles className="w-5 h-5 text-nexus-200" />
            <span className="text-nexus-200 text-sm font-medium">Nexus-360</span>
          </div>
          <h1 className="text-2xl sm:text-3xl font-bold mb-2">
            Bonjour{userName ? `, ${userName}` : ''} !
          </h1>
          <p className="text-nexus-100 text-base max-w-xl">
            {structureName
              ? `Bienvenue sur votre espace de gestion pour ${structureName}.`
              : 'Bienvenue sur votre plateforme de gestion multi-applications.'}
          </p>
        </div>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {statCards.map((stat) => {
          const Icon = stat.icon;
          return (
            <div
              key={stat.label}
              className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm hover:shadow-md transition-shadow"
            >
              <div className="flex items-center gap-4">
                <div className={`w-11 h-11 rounded-lg flex items-center justify-center ${stat.color}`}>
                  <Icon className="w-5 h-5" />
                </div>
                <div>
                  <p className="text-sm text-slate-500">{stat.label}</p>
                  <p className="text-xl font-bold text-slate-900">{stat.value}</p>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* App Launcher + Notifications */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* App Launcher */}
        <div className="lg:col-span-2 space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
              <AppWindow className="w-5 h-5 text-nexus-600" />
              Applications
            </h2>
            <Link to="/apps" className="text-sm text-nexus-600 hover:text-nexus-700 font-medium flex items-center gap-1">
              Voir tout <ExternalLink className="w-3.5 h-3.5" />
            </Link>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {apps.map((app) => {
              const isSubscribed = uniqueSubscribedCodes.includes(app.code);
              const detailedItem = subscriptions.find((s) => s.app_code === app.code);
              const sub = detailedItem?.nexus_subscription || detailedItem?.app_subscription;
              const AppIcon = APP_ICONS[app.code] || AppWindow;
              const statusInfo = sub ? (STATUS_BADGES[sub.status] || STATUS_BADGES.active) : null;
              const userAppRoleObj = user?.app_roles?.[app.code];
              const userAppRole = typeof userAppRoleObj === 'object' ? userAppRoleObj?.role : userAppRoleObj;

              return (
                <div
                  key={app.code || app.id}
                  className={`bg-white rounded-xl border p-5 transition-all ${
                    isSubscribed
                      ? 'border-slate-200 shadow-sm hover:shadow-md hover:border-nexus-200'
                      : 'border-slate-200 opacity-60'
                  }`}
                >
                  <div className="flex items-start gap-4">
                    <div className={`w-11 h-11 rounded-lg flex items-center justify-center ${
                      isSubscribed ? 'bg-nexus-50 text-nexus-600' : 'bg-slate-100 text-slate-400'
                    }`}>
                      <AppIcon className="w-5 h-5" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="font-semibold text-slate-900 text-sm truncate">{app.name || app.nom}</h3>
                        {statusInfo && (
                          <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${statusInfo.className}`}>
                            {statusInfo.label}
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-slate-500 line-clamp-2 mb-3">
                        {app.description || app.description_courte || 'Application Nexus'}
                      </p>
                      {userAppRole && (
                        <p className="text-xs text-slate-400 mb-3">
                          Role : <span className="font-medium text-slate-600">{userAppRole}</span>
                        </p>
                      )}
                      {isSubscribed && app.sso_mode === 'callback' ? (
                        <button
                          type="button"
                          onClick={() => openApp(app)}
                          disabled={launching === app.code}
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-nexus-600 text-white text-xs font-medium hover:bg-nexus-700 transition-colors disabled:opacity-60"
                        >
                          {launching === app.code ? (
                            <>
                              Ouverture… <Loader2 className="w-3 h-3 animate-spin" />
                            </>
                          ) : (
                            <>
                              Ouvrir <ExternalLink className="w-3 h-3" />
                            </>
                          )}
                        </button>
                      ) : isSubscribed ? (
                        <a
                          href={`${app.url || app.base_url || APP_URLS[app.code] || '#'}/dashboard?token=${localStorage.getItem('token') || ''}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-nexus-600 text-white text-xs font-medium hover:bg-nexus-700 transition-colors"
                        >
                          Ouvrir <ExternalLink className="w-3 h-3" />
                        </a>
                      ) : (
                        <Link
                          to="/apps"
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-slate-300 text-slate-600 text-xs font-medium hover:bg-slate-50 transition-colors"
                        >
                          <Eye className="w-3 h-3" />
                          Decouvrir
                        </Link>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
            {apps.length === 0 && (
              <div className="sm:col-span-2 bg-white rounded-xl border border-slate-200 p-8 text-center">
                <AppWindow className="w-10 h-10 text-slate-300 mx-auto mb-3" />
                <p className="text-sm text-slate-500">Aucune application disponible</p>
              </div>
            )}
          </div>
        </div>

        {/* Notifications Feed */}
        <div className="space-y-4">
          <h2 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
            <Bell className="w-5 h-5 text-nexus-600" />
            Notifications
          </h2>
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm divide-y divide-slate-100 max-h-[480px] overflow-y-auto">
            {notifications.length > 0 ? (
              notifications.map((notif, idx) => {
                const NIcon = APP_ICONS[notif.app_code] || Bell;
                return (
                  <div key={notif.id || idx} className="p-4 hover:bg-slate-50 transition-colors">
                    <div className="flex gap-3">
                      <div className="w-8 h-8 rounded-full bg-nexus-50 flex items-center justify-center flex-shrink-0">
                        <NIcon className="w-4 h-4 text-nexus-600" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-0.5">
                          <p className="text-sm font-medium text-slate-900 truncate">{notif.title || notif.titre}</p>
                          {notif.app_code && (
                            <span className="text-xs bg-slate-100 text-slate-500 px-1.5 py-0.5 rounded font-medium flex-shrink-0">
                              {notif.app_code}
                            </span>
                          )}
                        </div>
                        <p className="text-xs text-slate-500 line-clamp-2">{notif.message || notif.body}</p>
                        <p className="text-xs text-slate-400 mt-1 flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          {notif.created_at
                            ? new Date(notif.created_at).toLocaleDateString('fr-FR', {
                                day: 'numeric',
                                month: 'short',
                                hour: '2-digit',
                                minute: '2-digit',
                              })
                            : 'Recemment'}
                        </p>
                      </div>
                    </div>
                  </div>
                );
              })
            ) : (
              <div className="p-8 text-center">
                <Bell className="w-8 h-8 text-slate-300 mx-auto mb-2" />
                <p className="text-sm text-slate-500">Aucune notification</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Subscription Overview */}
      {subscriptions.length > 0 && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
              <CreditCard className="w-5 h-5 text-nexus-600" />
              Abonnements en cours
            </h2>
            <Link to="/subscriptions" className="text-sm text-nexus-600 hover:text-nexus-700 font-medium">
              Gerer
            </Link>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {subscriptions.map((item, idx) => {
              const appCode = item.app_code || '';
              const SubIcon = APP_ICONS[appCode] || AppWindow;
              const nexusSub = item.nexus_subscription;
              const appSub = item.app_subscription;
              const pkg = item.package;
              const subStatus = appSub?.status || nexusSub?.status || (item.is_subscribed ? 'active' : 'inactive');
              const statusInfo = STATUS_BADGES[subStatus] || STATUS_BADGES.active;
              const periodEnd = appSub?.current_period_end || nexusSub?.current_period_end;

              return (
                <div key={appCode || idx} className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm">
                  <div className="flex items-center gap-3 mb-3">
                    <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${item.is_subscribed ? 'bg-nexus-50' : 'bg-slate-100'}`}>
                      <SubIcon className={`w-4 h-4 ${item.is_subscribed ? 'text-nexus-600' : 'text-slate-400'}`} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="text-sm font-semibold text-slate-900 truncate">
                        {item.app_name || APP_NAMES[appCode] || appCode}
                      </h3>
                      <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${statusInfo.className}`}>
                        {statusInfo.label}
                      </span>
                    </div>
                  </div>
                  <div className="space-y-1.5 text-xs text-slate-500">
                    {(pkg?.name || nexusSub?.package_code) && (
                      <p>Pack : <span className="font-medium text-slate-700">{pkg?.name || nexusSub?.package_code}</span></p>
                    )}
                    {periodEnd && (
                      <p className="flex items-center gap-1">
                        <CalendarDays className="w-3 h-3" />
                        Prochaine echeance :{' '}
                        <span className="font-medium text-slate-700">
                          {new Date(periodEnd).toLocaleDateString('fr-FR')}
                        </span>
                      </p>
                    )}
                    {!item.is_subscribed && (
                      <p className="text-slate-400 italic">Acces via role applicatif</p>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default Dashboard;
